import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './common/Header';
import Footer from './common/Footer';
import { getValidSession } from './helpers/sessionUtils';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
    });
};

const categoryLabel = (cat) => {
    if (!cat) return '—';
    return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

function SuggestedVendors() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });

    useEffect(() => {
        const session = getValidSession();
        if (!session?.user) {
            navigate('/login');
            return;
        }
        fetchData(1, session.user.token);
        // eslint-disable-next-line
    }, []);

    const fetchData = async (page = 1, token) => {
        try {
            setLoading(true);
            setError(null);
            const authToken = token || localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/recommendations/customer/suggested-vendors`, {
                params: { page, limit: 10 },
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.data.success) {
                setSessions(response.data.data);
                setPagination({
                    page: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    totalItems: response.data.totalItems
                });
            }
        } catch (err) {
            setError('Failed to fetch suggested vendors. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <div className="sv-wrapper">
                <div className="sv-hero">
                    <div className="container">
                        <h1 className="sv-hero-title">Suggested Vendors</h1>
                        <p className="sv-hero-sub">
                            Vendors we recommended to you based on your past enquiries
                        </p>
                    </div>
                </div>

                <div className="container sv-content">
                    {loading ? (
                        <div className="sv-loading">
                            <div className="sv-spinner"></div>
                            <p>Loading your vendor recommendations...</p>
                        </div>
                    ) : error ? (
                        <div className="sv-empty sv-error">
                            <i className="bi bi-exclamation-triangle-fill sv-empty-icon"></i>
                            <h3>Something went wrong</h3>
                            <p>{error}</p>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="sv-empty">
                            <i className="bi bi-inbox sv-empty-icon"></i>
                            <h3>No Recommendations Yet</h3>
                            <p>When you enquire about a service, we'll send you vendor recommendations here.</p>
                            <a href="/" className="sv-explore-btn">Explore Services</a>
                        </div>
                    ) : (
                        <>
                            {sessions.map((session) => (
                                <div className="sv-session-card" key={session.id}>
                                    <div className="sv-session-header">
                                        <div>
                                            <span className="sv-session-category">{categoryLabel(session.category)}</span>
                                            <span className="sv-session-date">{formatDate(session.created_at)}</span>
                                        </div>
                                        <span className={`sv-session-badge ${session.status === 'completed' ? 'sv-badge-done' : 'sv-badge-active'}`}>
                                            {session.status === 'completed' ? 'Completed' : 'Active'}
                                        </span>
                                    </div>

                                    {session.history && session.history.length > 0 ? (
                                        <div className="sv-vendor-grid">
                                            {session.history.map((h) => (
                                                <div className="sv-vendor-card" key={h.id}>
                                                    <div className="sv-vendor-avatar">
                                                        {(h.vendor?.name || h.vendor?.company_name || 'V').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="sv-vendor-info">
                                                        <h4 className="sv-vendor-name">
                                                            {h.vendor?.company_name || h.vendor?.name || 'Vendor'}
                                                        </h4>
                                                        {h.vendor?.city && (
                                                            <p className="sv-vendor-city">
                                                                <i className="bi bi-geo-alt-fill"></i>
                                                                {h.vendor.city}
                                                            </p>
                                                        )}
                                                        {h.vendor?.phone && (
                                                            <a href={`tel:${h.vendor.phone}`} className="sv-vendor-phone">
                                                                <i className="bi bi-telephone-fill"></i>
                                                                {h.vendor.phone}
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="sv-vendor-batch">Batch {h.batch_no}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="sv-no-vendors">No vendors recorded for this session.</p>
                                    )}
                                </div>
                            ))}

                            {pagination.totalPages > 1 && (
                                <div className="sv-pagination">
                                    <button
                                        className="sv-page-btn"
                                        disabled={pagination.page <= 1}
                                        onClick={() => fetchData(pagination.page - 1)}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                        <button
                                            key={p}
                                            className={`sv-page-btn ${p === pagination.page ? 'sv-page-active' : ''}`}
                                            onClick={() => fetchData(p)}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        className="sv-page-btn"
                                        disabled={pagination.page >= pagination.totalPages}
                                        onClick={() => fetchData(pagination.page + 1)}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <Footer />

            <style>{`
                .sv-wrapper { background: #f8fafc; min-height: 80vh; }
                .sv-hero {
                    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
                    padding: 60px 0 40px;
                    color: #fff;
                }
                .sv-hero-title { font-size: 2rem; font-weight: 800; margin-bottom: 10px; }
                .sv-hero-sub { font-size: 1.05rem; opacity: 0.85; margin: 0; }
                .sv-content { padding: 40px 0 60px; max-width: 860px; }

                /* Loading */
                .sv-loading { text-align: center; padding: 80px 0; color: #64748b; }
                .sv-spinner {
                    width: 48px; height: 48px; border-radius: 50%;
                    border: 4px solid #e8f0fe;
                    border-top-color: #0d6efd;
                    animation: svSpin 0.8s linear infinite;
                    margin: 0 auto 20px;
                }
                @keyframes svSpin { to { transform: rotate(360deg); } }

                /* Empty state */
                .sv-empty { text-align: center; padding: 80px 0; color: #64748b; }
                .sv-empty-icon { font-size: 3.5rem; margin-bottom: 20px; display: block; color: #94a3b8; }
                .sv-empty h3 { font-size: 1.4rem; color: #1e293b; margin-bottom: 10px; }
                .sv-empty p { margin-bottom: 24px; }
                .sv-error .sv-empty-icon { color: #dc3545; }
                .sv-explore-btn {
                    display: inline-block; padding: 12px 30px; background: #0d6efd;
                    color: #fff; border-radius: 50px; text-decoration: none; font-weight: 600;
                    transition: background 0.2s;
                }
                .sv-explore-btn:hover { background: #0a58ca; color: #fff; }

                /* Session card */
                .sv-session-card {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                    margin-bottom: 28px;
                    overflow: hidden;
                    transition: box-shadow 0.2s;
                }
                .sv-session-card:hover { box-shadow: 0 6px 24px rgba(0,0,0,0.10); }
                .sv-session-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 18px 24px; border-bottom: 1px solid #f0f4ff;
                    background: #f8fbff;
                    flex-wrap: wrap; gap: 8px;
                }
                .sv-session-category {
                    font-weight: 700; color: #1e293b; font-size: 15px; margin-right: 12px;
                }
                .sv-session-date { font-size: 13px; color: #94a3b8; }
                .sv-session-badge {
                    font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 20px;
                }
                .sv-badge-active { background: #e8f9ee; color: #25d366; }
                .sv-badge-done   { background: #e8f0fe; color: #0d6efd; }

                /* Vendor grid */
                .sv-vendor-grid {
                    padding: 20px 24px;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 16px;
                }
                .sv-vendor-card {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 16px;
                    position: relative;
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .sv-vendor-card:hover { border-color: #0d6efd; box-shadow: 0 4px 12px rgba(13,110,253,0.08); }
                .sv-vendor-avatar {
                    width: 44px; height: 44px; border-radius: 50%;
                    background: linear-gradient(135deg, #0d6efd, #6610f2);
                    color: #fff; font-weight: 800; font-size: 18px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .sv-vendor-info { flex: 1; min-width: 0; }
                .sv-vendor-name { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
                .sv-vendor-city { font-size: 12px; color: #64748b; margin: 0 0 4px; display: flex; align-items: center; gap: 4px; }
                .sv-vendor-phone {
                    font-size: 13px; color: #0d6efd; text-decoration: none;
                    font-weight: 600; display: flex; align-items: center; gap: 4px;
                }
                .sv-vendor-phone:hover { text-decoration: underline; }
                .sv-vendor-batch {
                    position: absolute; top: 10px; right: 12px;
                    font-size: 11px; font-weight: 600; color: #64748b; background: #f1f5f9;
                    padding: 2px 8px; border-radius: 20px;
                }
                .sv-no-vendors { padding: 20px 24px; color: #94a3b8; font-style: italic; }

                /* Pagination */
                .sv-pagination { display: flex; justify-content: center; gap: 8px; margin-top: 32px; flex-wrap: wrap; }
                .sv-page-btn {
                    min-width: 38px; height: 38px; border-radius: 8px;
                    border: 1px solid #e2e8f0; background: #fff;
                    font-size: 14px; font-weight: 600; color: #475569;
                    cursor: pointer; display: flex; align-items: center; justify-content: center;
                    transition: all 0.15s;
                }
                .sv-page-btn:hover:not(:disabled) { border-color: #0d6efd; color: #0d6efd; }
                .sv-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
                .sv-page-active { background: #0d6efd; color: #fff; border-color: #0d6efd; }
                .sv-page-active:hover { color: #fff; }

                @media (max-width: 576px) {
                    .sv-hero-title { font-size: 1.5rem; }
                    .sv-vendor-grid { grid-template-columns: 1fr; }
                }
            `}</style>
        </>
    );
}

export default SuggestedVendors;
